import { del, get, head, patch, post, put } from "aws-amplify/api";

// Compatibility wrapper that exposes the AWS Amplify v4 `API` surface
// (`API.get(name, path, init)`) on top of Amplify v6's modular API functions.
//
// Why this exists:
//   The v4 → v6 upgrade renames every API method and changes the response
//   shape (response is now a wrapper around the HTTP body — call sites used
//   to receive the parsed JSON directly). Codemodding ~130 call sites is
//   risky; instead we keep the original ergonomic shape and translate it
//   here, in one place.
//
// What changes between v4 and v6 are handled here:
//   - `queryStringParameters` → `queryParams` (and values must be strings).
//   - The response is unwrapped (`.response.body.json()`) and returned
//     directly, mirroring v4's "Promise<any>" shape.
//   - Empty / non-JSON responses fall back to `text()` then `undefined`.
//   - `null` / `undefined` query param values are dropped (v6 would reject
//     them; v4 silently omitted them).
//
// Authentication: the API Gateway endpoints used by this app are protected
// with AWS_IAM authorization. Amplify v6 transparently SigV4-signs every
// API Gateway request using Cognito Identity Pool credentials obtained via
// `fetchAuthSession()`. We deliberately do NOT inject an `Authorization`
// header here — v6 skips its native SigV4 signer if any `authorization`
// header is already set on the request, and API Gateway would then reject
// the raw JWT as a malformed SigV4 signature.
//
// The wrapper does NOT support the v4 `{ response: true }` option, because
// the codebase does not use it.

const toV6Options = (init) => {
  if (!init || typeof init !== "object") return {};
  const { queryStringParameters, body, headers, ...rest } = init;
  const options = { ...rest };

  if (queryStringParameters && typeof queryStringParameters === "object") {
    const entries = Object.entries(queryStringParameters).flatMap(([k, v]) => {
      if (v === undefined || v === null) return [];
      if (Array.isArray(v)) return [[k, v.map(String).join(",")]];
      return [[k, String(v)]];
    });
    if (entries.length > 0) {
      options.queryParams = Object.fromEntries(entries);
    }
  }

  if (body !== undefined) options.body = body;
  if (headers !== undefined) options.headers = headers;
  return options;
};

const readBody = async (response) => {
  if (!response?.body) return undefined;
  try {
    return await response.body.json();
  } catch {
    try {
      return await response.body.text();
    } catch {
      return undefined;
    }
  }
};

function extractErrorText(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const t = value.trim();
    if (!t) return null;
    try {
      const parsed = JSON.parse(t);
      return (
        parsed.error ||
        parsed.message ||
        parsed.reason ||
        (typeof parsed === "string" ? parsed : t)
      );
    } catch {
      return t.length <= 400 ? t : `${t.slice(0, 400)}…`;
    }
  }
  if (typeof value === "object") {
    return (
      value.error ||
      value.message ||
      value.reason ||
      (value.data && extractErrorText(value.data)) ||
      null
    );
  }
  return null;
}

/** Best-effort message from Amplify v6 RestApiError / network failures. */
export function apiErrorMessage(err, fallback = "Kërkesa dështoi") {
  if (!err) return fallback;
  const fromBody =
    extractErrorText(err?.response?.data) ||
    extractErrorText(err?.response?.body) ||
    extractErrorText(err?.data) ||
    extractErrorText(err?.body) ||
    extractErrorText(err?.cause);
  if (fromBody) return String(fromBody);
  const msg = err?.message ? String(err.message) : "";
  if (msg && !/^unknown error$/i.test(msg)) return msg;
  return fallback;
}

async function attachParsedErrorBody(err) {
  const resp = err?.response;
  if (!resp || resp.data != null || resp.body == null) return err;
  try {
    let raw = resp.body;
    if (typeof raw === "string") {
      /* already text */
    } else if (typeof raw?.text === "function") {
      raw = await raw.text();
    } else if (typeof Blob !== "undefined" && raw instanceof Blob) {
      raw = await raw.text();
    } else if (raw instanceof ArrayBuffer) {
      raw = new TextDecoder().decode(raw);
    } else if (typeof raw === "object") {
      resp.data = raw;
      return err;
    }
    if (typeof raw === "string" && raw.trim()) {
      try {
        resp.data = JSON.parse(raw);
      } catch {
        resp.data = { error: raw };
      }
    }
  } catch {
    /* ignore */
  }
  return err;
}

const wrap = (fn) => async (apiName, path, init) => {
  try {
    const op = fn({ apiName, path, options: toV6Options(init) });
    const response = await op.response;
    const data = await readBody(response);
    // Some Lambdas return HTTP 200 with { status: false } — treat as failure.
    if (
      data &&
      typeof data === "object" &&
      data.status === false &&
      (data.error || data.message)
    ) {
      const err = new Error(data.error || data.message || "request_failed");
      err.response = { data };
      throw err;
    }
    return data;
  } catch (err) {
    await attachParsedErrorBody(err);
    if (
      err?.message &&
      /^unknown error$/i.test(err.message) &&
      err?.response?.data
    ) {
      const better = extractErrorText(err.response.data);
      if (better) err.message = better;
    }
    throw err;
  }
};

export const API = {
  get: wrap(get),
  post: wrap(post),
  put: wrap(put),
  del: wrap(del),
  patch: wrap(patch),
  head: wrap(head),
};

export default API;
